import { useTranslation } from "react-i18next";
import Checkbox from "reusableComponents/Checkbox";
import styles from "./styles.module.scss";

// Generate time slots (00:00, 00:30, ..., 23:30)
const allTimeSlots = Array.from({ length: 24 }, (_, i) => `${i}:00`);
// Display from 07:00 to 24:00
const timeSlots = allTimeSlots.slice(7);

const WorkingHoursTable = ({ workingHours, setWorkingHours }) => {
  const { t } = useTranslation("global");

  // Toggle working hours for a specific time slot
  const handleSlotClick = (day, index) => {
    setWorkingHours((prevHours) => {
      const prevDay = prevHours[day];
      const slotIndex = index + 14; // Adjust for the offset

      // Toggle the clicked slot
      const newSlots = prevDay.slots
        .split("")
        .map((bit, i) => (i === slotIndex ? (bit === "0" ? "1" : "0") : bit))
        .join("");

      return {
        ...prevHours,
        [day]: { ...prevDay, slots: newSlots },
      };
    });
  };

  // Toggle whether a day is a working day
  const toggleWorkingDay = (day) => {
    setWorkingHours((prevHours) => {
      const prevDay = prevHours[day];
      const isWorkingDay = !prevDay.isWorkingDay;

      // Set default working hours (09:00 - 17:00) or clear slots
      const newSlots = isWorkingDay
        ? "0".repeat(18) + "1".repeat(17) + "0".repeat(13) // 09:00 to 17:00 (17 slots for 8.5 hours? Let me calculate properly)
        : "0".repeat(48); // Non-working day
      
      // Let's calculate the correct pattern:
      // Each slot is 30 minutes, so 8 hours = 16 slots, 9 hours = 18 slots
      // 9:00 to 17:00 is 8 hours = 16 slots
      // Slots from 07:00 (index 14) to 24:00 (index 48)
      // 09:00 is at index 18 (7:00=14, 7:30=15, 8:00=16, 8:30=17, 9:00=18)
      // 17:00 is at index 34 (17:00 = 34 because: 7:00=14, 8:00=16, 9:00=18, 10:00=20, 11:00=22, 12:00=24, 13:00=26, 14:00=28, 15:00=30, 16:00=32, 17:00=34)
      // So we need 1's from index 18 to 34 (inclusive) = 17 slots (8.5 hours)
      // 9:00-17:00 is actually 8 hours, so it should be index 18 to 33 (16 slots)
      
      // Corrected: 9:00 (index 18) to 17:00 (index 33) = 16 slots (8 hours)
      const workingSlotsPattern = "0".repeat(18) + "1".repeat(16) + "0".repeat(14); // 18 + 16 + 14 = 48

      return {
        ...prevHours,
        [day]: {
          ...prevDay,
          isWorkingDay,
          slots: isWorkingDay ? workingSlotsPattern : "0".repeat(48),
        },
      };
    });
  };

  // Translate day name - handle day_0 as Sunday properly
  const getTranslatedDayName = (dayKey, originalName) => {
    // Map day_0 to Sunday, day_1 to Monday, etc.
    const dayIndex = parseInt(dayKey.split('_')[1]);
    
    // Use translations based on day index
    const dayTranslations = {
      0: t('days.short.sunday', 'Sunday'),
      1: t('days.short.monday', 'Monday'),
      2: t('days.short.tuesday', 'Tuesday'),
      3: t('days.short.wednesday', 'Wednesday'),
      4: t('days.short.thursday', 'Thursday'),
      5: t('days.short.friday', 'Friday'),
      6: t('days.short.saturday', 'Saturday'),
    };
    
    return dayTranslations[dayIndex] || originalName;
  };

  // Reorder days to show Monday first, Sunday last
  const getOrderedDays = () => {
    const entries = Object.entries(workingHours);
    // Start from Monday (index 1) through Saturday (index 6), then Sunday (index 0) at the end
    return [
      ...entries.slice(1, 7), // Monday (index 1) to Saturday (index 6)
      entries[0], // Sunday (index 0) at the end
    ];
  };

  return (
    <div className={styles.timetableContainer}>
      <div className={styles.timetable}>
        {/* Header Row for Time Slots */}
        <div className={styles.headerRow}>
          <div className={styles.emptyCell}></div>
          {timeSlots.map((time, index) => (
            <div key={index} className={styles.hourCell}>
              {time}
            </div>
          ))}
        </div>

        {/* Rows for Each Day - Monday to Saturday, then Sunday at the end */}
        {getOrderedDays().map(([dayKey, { name, slots, isWorkingDay }]) => (
          <div key={dayKey} className={styles.row}>
            <div className={styles.weekday}>
              <Checkbox
                checked={isWorkingDay}
                onChange={() => toggleWorkingDay(dayKey)}
                iconType
                isSquare
                label={getTranslatedDayName(dayKey, name)}
              />
            </div>
            <div className={styles.slots}>
              {slots.slice(14).split("").map((slot, i) => (
                <div
                  key={i}
                  className={`${styles.slot} ${slot === "1" ? styles.working : styles.nonWorking}`}
                  onClick={() => isWorkingDay && handleSlotClick(dayKey, i)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Color Guide */}
      <div className={styles.colorGuide}>
        <div className={styles.colorBox} style={{ backgroundColor: "#72ccdf" }}></div>
        <div className={styles.colorLabel}>{t("provide.step3.timetable.availableHours", "Working Hours")}</div>
        <div className={styles.colorBox} style={{ backgroundColor: "#e3e3e3" }}></div>
        <div className={styles.colorLabel}>{t("provide.step3.timetable.unavailableHours", "Non-working Hours")}</div>
      </div>
    </div>
  );
};

export default WorkingHoursTable;